const CRYPTO_USAGES = ['sign'];
const CRYPTO_EXPORTABLE = false;
const ALGORITHM_TYPE = 'HMAC';

/**
 * Use SubtleCrypto to sign the server request
 * https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/sign
 * @param {Object} requestBody
 */
export default async function getRequestSignature(requestBody) {
  if (!window.Chime || !window.Chime.a) {
    return null;
  }

  const cryptoKey = await getCryptoKey();
  // Encode the request body. If there's no request body, we must encode an empty string for the encryption to work.
  const encodedRequestBody = stringToArrayBuffer(requestBody || '');

  const signature = await crypto.subtle.sign(
    ALGORITHM_TYPE,
    cryptoKey,
    encodedRequestBody
  );

  return encode(arrayBufferToString(signature));
}

/** FORMATTING/HELPER FUNCTIONS */

/**
 * Creates a CryptoKey object from the key string passed through the JWT.
 * https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/importKey
 */
async function getCryptoKey() {
  if (window.Chime.k) {
    return window.Chime.k;
  }

  const jwt = window.Chime.a;
  const jwtComponentsArray = jwt.split('.');

  // Create algorithm object
  const jwtHeaderString = jwtComponentsArray[0];
  const cryptoAlgorithmObject = formatCryptoAlgorithm(jwtHeaderString);

  // Parse and format key from JWT
  const jwtBodyString = jwtComponentsArray[1];
  const keyBuffer = formatCryptoKeyData(jwtBodyString);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBuffer,
    cryptoAlgorithmObject,
    CRYPTO_EXPORTABLE,
    CRYPTO_USAGES
  );

  window.Chime.k = cryptoKey;

  return cryptoKey;
}

/**
 * Formats the algorithm parameter to pass into the creation of a CryptoKey object
 * @param {String} jwtHeader
 */
function formatCryptoAlgorithm(jwtHeader) {
  // window.atob decodes string with base 64
  // https://stackoverflow.com/a/30106551
  const decodedHeader = window.atob(jwtHeader);
  const headerObject = JSON.parse(decodedHeader);

  // the header of the JWT has an object that indicates the algorithm
  // HS256 --> SHA-256
  const jwtAlgorithm = headerObject.alg;
  const hashAlgorithm = jwtAlgorithm.replace('HS', 'SHA-');

  const cryptoAlgorithmObject = {
    name: ALGORITHM_TYPE,
    hash: { name: hashAlgorithm },
  };

  return cryptoAlgorithmObject;
}

/**
 * Formats the key data to pass into the creation of a CryptoKey object
 * @param {String} jwtBody
 */
function formatCryptoKeyData(jwtBody) {
  const decodedBody = decode(jwtBody);
  const jwtKey = JSON.parse(decodedBody).key;
  const keyAsArrayBuffer = stringToArrayBuffer(decode(jwtKey));

  return keyAsArrayBuffer;
}

function stringToArrayBuffer(str) {
  const stringLength = str.length;
  const buffer = new ArrayBuffer(stringLength * 2);
  const bufferView = new Uint8Array(buffer);

  for (let i = 0; i < stringLength; i++) {
    bufferView[i] = str.charCodeAt(i);
  }

  return buffer;
}

function arrayBufferToString(buffer) {
  return String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer)));
}

function encode(s) {
  const escaped = encodeUnicode(s);
  const base64 = btoa(escaped);

  return base64.replace(/=/g, '').replace(/\//g, '_').replace(/\+/g, '-');
}

function encodeUnicode(s) {
  return encodeURIComponent(s).replace(/%([0-9A-F]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
}

function decode(s) {
  const base64 = pad(s).replace(/_/g, '/').replace(/-/g, '+');
  return decodeUnicode(atob(base64));
}

function decodeUnicode(s) {
  try {
    return decodeURIComponent(
      s.replace(/(.)/g, (_, p) => {
        const code = p.charCodeAt(0).toString(16).toUpperCase();
        if (code.length < 2) {
          return `%0${code}`;
        }
        return `%${code}`;
      })
    );
  } catch {
    return s;
  }
}

function pad(s) {
  switch (s.length % 4) {
    case 0:
      return s;
    case 2:
      return `${s}==`;
    case 3:
      return `${s}=`;
    default:
      throw new Error('Illegal base64url string!');
  }
}
